import React, { useMemo, useState, useEffect, useContext, useCallback } from 'react';
import _ from 'lodash';
import { WizardPage, wizardProps } from '@xengage/gw-portals-wizard-react';
import { ViewModelForm, ViewModelServiceContext, useDataRefresh } from '@xengage/gw-portals-viewmodel-react';
import { FormattedMessage, useTranslator } from '@jutro/locale';
import { useDependencies } from '@xengage/gw-portals-dependency-react';
import { useAuthentication } from '@xengage/gw-digital-auth-react';
import { useValidation } from '@xengage/gw-portals-validation-react';
import { MLPublicContactsService } from 'gw-capability-quoteandbind';
import { MLLocalDateUtil, MLProductCodeUtil } from 'ml-portals-util-js';
import { MLDriverVehicleUtil, MLNotificationBlockUtil } from 'ml-portals-util-react';
import { MLModalService, MLTagManagerService } from 'ml-portals-services-react';
import { PolicyChange } from 'gw-capability-policychange-common-react';
import { platformMessages } from 'ml-components-platform-react';
import { MLNavigationStepsTrackingService } from 'ml-services-common-react';
import appConfig from 'app-config';
import PolChangeTrackEvent from 'gw-capability-gateway-quoteandbind-common-react/components/TrackEvent/PolChangeTrackEvent.jsx';
import metadata from './AdditionalInfoToPurchasePagePolChange.metadata.json5';
import { AccountService } from 'gw-capability-gateway-policy';
import { Identify  } from '../../../gw-capability-gateway-quoteandbind-common-react/components/TrackEvent/TrackEvent';

import messages from './AdditionalInfoToPurchasePagePolChange.messages';
import styles from './AdditionalInfoToPurchasePagePolChange.module.scss';
import { ScrollToError } from '@jutro/legacy/wizard-next';

const DEFENSIVE_DRIVER_PATTERN_CODE = 'PADefensiveDriver_ML';
const PRIMARY_VEHICLE_QUESTION_AVAILABLE_STATES = new Set(['NJ', 'CO']);

const AdditionalInfoToPurchasePagePolChange = (props) => {
    const { wizardData, goNext, updateWizardData, currentStep } = props;
    const { submissionVM: endorsementVM } = wizardData;
    const isGPA = appConfig.portalCode === 'GPA';

    const canEditCommunicationsFields = endorsementVM.canEditCommunicationsFields_Ext.value
    const translator = useTranslator();
    const viewModelService = useContext(ViewModelServiceContext);
    const { EndorsementService } = useDependencies('EndorsementService');
    const { authHeader } = useAuthentication();
    const { onValidate, isComponentValid } = useValidation('AdditionalInformationToPurchasePage');
    const { refreshData } = useDataRefresh();

    const [showErrors, setShowErrors] = useState(false);
    const [errorTimestamp, setErrorTimestamp] = useState(0);
    const [additionalInterestAvailableCandidatesML, setAdditionalInterestAvailableCandidatesML] = useState([]);
    
    const isModernProduct = _.get(endorsementVM, "isModernProduct_Ext.value", false);
    const isModernProductPersonalAuto = isModernProduct && MLProductCodeUtil.isPersonalAuto(_.get(endorsementVM, 'baseData.productCode.value'));
    
    const {
        clearBlockAlerts,
        addErrorAlert,
        getNumberOfAlertsExcludingBlocks,
        clearAlerts,
        clearErrorAlerts,
        addWarningAlert
    } = MLNotificationBlockUtil();

    const {
        showProgressDialog,
        hideProgressDialog
    } = MLModalService()

    const {
        hasPALeaseLoanCovButChangedOwnership,
        isFinanceWithoutPALeaseLoanCovButNoLienHolder,
        isLeaseButNoLessor,
        isOwnedButWithLienHolderLessor,
        validateVehiclesSerialNumber
    } = MLDriverVehicleUtil();

    const isPrimaryDriver = (driverVM) => {
        return driverVM.isPolicyHolder.value;
    };

    const isRatedDriver = (driverVM) => {
        return driverVM.type_ML.value.code === 'rated';
    };

    const isExcludedDriver = (driverVM) => {
        return driverVM.type_ML.value.code === 'excluded';
    };

    const isHouseholdDriver = (driverVM) => {
        return driverVM.type_ML.value.code === 'household';
    };

    const isInGA = (addressVM) => {
        return addressVM.state.value.code === 'GA';
    };

    const isInCO = (addressVM) => {
        return addressVM.state.value.code === 'CO';
    };

    const isInUT = (addressVM) => {
        return addressVM.state.value.code === 'UT';
    };

    const isInOR = (addressVM) => {
        return addressVM.state.value.code === 'OR';
    };

    const paModel = endorsementVM.lobData.personalAuto;
    const vehiclesVM = useMemo(() => {
        return paModel.coverables.vehicles.children.map((vehicleVM) => {
            vehicleVM.isSerialNumberRequired_ML.value = true;
            vehicleVM.value.makeAddlInfoReq_Ext = true;
            return vehicleVM;
        });
    }, [paModel]);

    const addressVM = endorsementVM.baseData.policyAddress;

    const driversVM = useMemo(() => {
        const getDriverSortPriority = (driverVM) => {
            if (isPrimaryDriver(driverVM)) {
                return 0;
            }
            switch (driverVM.type_ML.value.code) {
                case 'rated':
                    return 1;
                case 'household':
                    return 2;
                case 'excluded':
                    return 3;
                default:
                    return 9;
            }
        };

        const driversToSort = [...paModel.coverables.drivers.children];
        return driversToSort.sort((veh1VM, veh2VM) => {
            const veh1Priority = getDriverSortPriority(veh1VM);
            const veh2Priority = getDriverSortPriority(veh2VM);

            if (veh1Priority < veh2Priority) {
                return -1;
            }

            if (veh1Priority > veh2Priority) {
                return 1;
            }

            return 0;
        });

    }, [paModel]);
    
    const isElectronicChosen = useMemo(() => {
        if (endorsementVM.baseData.policyBillingDocCommPref_ML.value) {
            return endorsementVM.baseData.policyBillingDocCommPref_ML.value.code === 'electronic';
        }
        return false;
    }, [endorsementVM]);

    const hasNamedNonOwner = paModel.value.hasNamedNonOwner();

    const primaryDriver = useMemo(() => driversVM.find((driver) => isPrimaryDriver(driver)), [driversVM]);
    const primaryDriverEmail = _.get(primaryDriver, 'person.emailAddress_ML');

    const isSpouseDriver = (driverVM) => {
        return driverVM.relationshipToInsured_ML.value.code === 'spouse';
    };

    const primaryDriverSpouseIndex = _.findIndex(driversVM, (driver) => isSpouseDriver(driver));
    const vehiclesEligibleAsPrimaryVehicles = useMemo(() => vehiclesVM.map((v) => v.value).filter((vehicle) => vehicle.category_ML !== 'trailer'), [vehiclesVM]);

    const [potentialContacts, setPotentialContacts] = useState([]);
    const [hidePage, setHidePage] = useState(true);

    const initPrimaryVehicleQuestion = () => {
        const setPrimaryVehicleQuestionAvailable = (driver) => {
            const driverModifiersVM = driver.driverModifiers_ML;
            driver.showPrimaryVehicleQuestion = true;
            driverModifiersVM.isPrimaryVehicleRequired_ML.value = true;
            if (driverModifiersVM.primaryVehicleFixedId.value === 0) {
                driverModifiersVM.primaryVehicleFixedId.value = undefined;
            }
        };

        const state = addressVM.state.value.code;
        const ratedDrivers = driversVM.filter((driver) => isRatedDriver(driver));
        if (PRIMARY_VEHICLE_QUESTION_AVAILABLE_STATES.has(state)) {
            if (ratedDrivers.length === 1) {
                const matchingDriverVM = ratedDrivers[0];
                const driverModifiersVM = matchingDriverVM.driverModifiers_ML;
                if (vehiclesEligibleAsPrimaryVehicles.length === 1) {
                    driverModifiersVM.value.primaryVehicleFixedId = vehiclesEligibleAsPrimaryVehicles[0].getFixedId();
                }
                if (state === 'NJ') {
                    setPrimaryVehicleQuestionAvailable(matchingDriverVM);
                }
            } else if (ratedDrivers.length > 1) {
                ratedDrivers.forEach((driver) => {
                    const driverModifiersVM = driver.driverModifiers_ML;
                    const paDefensiveDriverMod = driverModifiersVM.modifiers.value.find((mod) => mod.patternCode === DEFENSIVE_DRIVER_PATTERN_CODE);
                    if (paDefensiveDriverMod && paDefensiveDriverMod.chosenTermValue === 'true') {
                        setPrimaryVehicleQuestionAvailable(driver);
                    }
                });
            }
        }
    };

    const getVehicleName = (vehicleVM) => {
        const isVehiclePrivateOrClassic = () => {
            if (_.isNil(vehicleVM.type_ML.value)) {
                return false;
            }
            switch (vehicleVM.type_ML.value.code) {
                case 'private_passenger_autos_ml':
                case 'classic_ml':
                    return true;
                default:
                    return false;
            }
        };

        if (isVehiclePrivateOrClassic()) {
            return `${vehicleVM.value.getYearMakeModelNameML(translator)} ${vehicleVM.trim_ML.value}`;
        }

        return vehicleVM.value.getYearMakeModelNameML(translator);
    };

    const isCategoryTrailerGolfCartOrLowSpeed = (vehicleVM) => {
        if (!vehicleVM.category_ML.value) {
            return false;
        }
        switch (vehicleVM.category_ML.value.code) {
            case 'trailer':
            case 'registered_golf_cart':
            case 'low_speed_vehicle':
            case 'Other_Personally_Registered':
            case 'trailer_np':
            case 'camper_np':
                return true;
            default:
                return false;
        }
    };

    const isFinancedOrLeased = (vehicleVM) => {
        return vehicleVM.value.financeCategory_ML === 'financed' || vehicleVM.value.financeCategory_ML === 'leased';
    };

    const getDriverFullName = (driverVM) => {
        const getSuffixName = (suffixCode) => {
            const suffix = viewModelService.productMetadata.get('pc').types.getTypelist('NameSuffix').getCode(suffixCode).name;
            if(suffix) {
                return translator({
                    id: suffix,
                    defaultMessage: suffix
                });
            }
            return "";
        };

        const driver = driverVM.person.value;
        const middleName = driver.middleName || '';
        const suffix = driver.suffix ? getSuffixName(driver.suffix) : '';
        return `${driver.firstName} ${middleName} ${driver.lastName} ${suffix}`;
    };

    const showDriverEmailAddress = (driverVM) => {
        if (isPrimaryDriver(driverVM)) {
            return true;
        }
        if (isSpouseDriver(driverVM) && isInCO(addressVM)) {
            return true;
        }
        if (isExcludedDriver(driverVM) && (isInGA(addressVM) || isInUT(addressVM) || isInOR(addressVM))) {
            return true;
        }
        return false;
    };

    const disableDriverEmailField = (driverVM) => {
        return (!canEditCommunicationsFields && driverVM.value.storedEmailAddress_ML);
    };

    const disableSpouseEmailAddress = () => {
        const spouseEmail =_.get(endorsementVM, `lobData.personalAuto.coverables.drivers.children[${primaryDriverSpouseIndex}].value.storedEmailAddress_ML`)
        return (!canEditCommunicationsFields && spouseEmail);
    };

    const showSpouseEmailAddress = () => {
        const isSpouseHouseholdDriver = () => {
            return primaryDriverSpouseIndex !== -1 && isHouseholdDriver(driversVM[primaryDriverSpouseIndex]);
        }; 

        return isInCO(addressVM) && isSpouseHouseholdDriver();
    };
    
    const isVinDuplicated = useCallback((currentVehicle) => {
        const vehicles = _.get(endorsementVM, 'lobData.personalAuto.coverables.vehicles.children', []);
        const vehicleVin = currentVehicle.vin;
        const vehiclesWithTheSameVin = vehicles ? vehicles.filter(
            (x) => !_.isUndefined(x.vin.value) && x.vin.value === vehicleVin.value
        ) : [];
        return vehiclesWithTheSameVin.length >= 2;
    }, [endorsementVM]);

    const checkForAddlInterests = () => {
        vehiclesVM.forEach((veh) => {
            if (_.isNil(veh.makeAddlInfoReq_Ext.value)) {
                veh.value.makeAddlInfoReq_Ext = true;
            }
            if (isFinancedOrLeased(veh)) {
                if (veh.additionalInterests.value.length < 1) {
                    const errorMessage = translator(messages.updateFieldOrAddInterestedParty);
                    addErrorAlert(errorMessage, veh.value.publicID);
                } else {
                    clearBlockAlerts(veh.value.publicID);
                }
            } else {
                clearBlockAlerts(veh.value.publicID);
            }
        });
    };

    const showVehicleOwnershipErrorMessages = () => {
        if (!!vehiclesVM && vehiclesVM.length > 0) {
            vehiclesVM.forEach((vehicleVM) => {
                let errorMsg = null;
                let errorName = null;

                if (hasPALeaseLoanCovButChangedOwnership(vehicleVM.value)) {
                    errorMsg = translator(messages.removeLeaseLoanGap);
                    errorName = 'removeLeaseLoanGap';
                }
                if (isFinanceWithoutPALeaseLoanCovButNoLienHolder(vehicleVM.value)) {
                    errorMsg = translator(messages.updateFieldOrAddInterestedParty);
                    errorName = 'noInterestedPartyInformation';
                }
                if (isLeaseButNoLessor(vehicleVM.value)) {
                    errorMsg = translator(messages.updateFieldOrAddInterestedParty);
                    errorName = 'leasedWithNoLeasingcompany';
                }
                if (isOwnedButWithLienHolderLessor(vehicleVM.value)) {
                    errorMsg = translator(messages.updateOwnedFinanceLeasedField);
                    errorName = 'updateOwnedFinanceLeasedField';
                }
                if (!!errorMsg && !!errorName) {
                    addErrorAlert(errorMsg, errorName);
                } else {
                    clearBlockAlerts(errorName);
                }
            });
        }
    };

    const vehiclesExistWithYearPre1981_ML = useCallback(() => {
        return vehiclesVM.some((vehicle) => {
            return vehicle.year.value < 1981;
        });
    }, [vehiclesVM]);

    const checkIfVinsVerified = () => {
        return !vehiclesVM.some((vehicle) => {
            return (!isCategoryTrailerGolfCartOrLowSpeed(vehicle) && !vehicle.isValidVin);
        });
    };

    const checkForUniqueLicenseNumbers = () => {
        clearBlockAlerts('duplicateLicense');

        const drivers = endorsementVM.lobData.personalAuto.coverables.drivers.value.filter((d) => {
            return d.licenseNumber && (d.type_ML === 'rated' || d.type_ML === 'excluded');
        });
        const seen = new Set();
        let hasDuplicate = false;

        drivers.forEach((d) => {
            if (seen.has(d.licenseNumber)) {
                hasDuplicate = true;
            } else {
                seen.add(d.licenseNumber);
            }
        });

        if(hasDuplicate) {
            addErrorAlert(messages.duplicateLicenseError, 'duplicateLicense');
            
            return false;
        }

        return true;
    };

    const isPageValid = () => {
        if (!endorsementVM.aspects.valid || !endorsementVM.aspects.subtreeValid) {
            return false;
        }

        if (!validateVehiclesSerialNumber(vehiclesVM)) {
            return false;
        }
        checkForAddlInterests();
        showVehicleOwnershipErrorMessages();

        if (!checkForUniqueLicenseNumbers()) {
            return false;
        }
        
        if (getNumberOfAlertsExcludingBlocks(['duplicateLicense', 'invalidEmail']) > 0) {
            window.scrollTo(0, 0);
            return false;
        }
        
        return true;
    };

    const checkAllRequiredVinsLookedUp = () => {
        return vehiclesVM.every((vehicle) => {
            return (isCategoryTrailerGolfCartOrLowSpeed(vehicle) || !_.isNil(vehicle.isValidVin));
        });
    };

    const saveAndQuote = () => {
        endorsementVM.value.portalCurrentStep_ML = currentStep.mlStepName;
        const promise = EndorsementService.saveAndQuote(endorsementVM.value, authHeader);
        showProgressDialog(platformMessages.quotingPolicyChange, promise);
        promise.then((responseData) => {
            MLNavigationStepsTrackingService.stepChange(null, currentStep.mlStepName);
            MLTagManagerService.sendFormStepPolicyChangeStep();
            endorsementVM.value = new PolicyChange(responseData);
            updateWizardData(wizardData);
            goNext();
        });
    }

    const verifyVinsOnLoad = () => {
        const intervalPromise = new Promise((resolve, reject) => {
            let repetitions = 12;
            const vinInterval = setInterval(() => {
                if (checkAllRequiredVinsLookedUp()) {
                    clearInterval(vinInterval);
                    if (checkIfVinsVerified()) {
                        resolve();
                    } else {
                        reject();
                    }
                }

                repetitions -= 1;
                if (repetitions === 0) {
                    clearInterval(vinInterval);
                    reject();
                }
            }, 250);
        });    
        showProgressDialog(messages.verifyingVins, intervalPromise, true, 0);
        intervalPromise.then(() => {
            saveAndQuote();
        }, () => {
            setHidePage(false)
        });
    };

    const verifyAndSkipPage = () => {
        if (isPageValid()) {
            if (!hasNamedNonOwner) {
                verifyVinsOnLoad();
            } else {
                saveAndQuote();
            }
            return;
        }    
        setHidePage(false);            
    };
    useEffect(() => {
        Identify('Endorsement Step Reached', {step: 'Additional Info to Purchase Screen'}, endorsementVM, authHeader)
    }, [])

    useEffect(() => {
        MLNavigationStepsTrackingService.stepChange(currentStep.mlStepName);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (isGPA) {
            PolChangeTrackEvent('Endorsement Step Reached', false, endorsementVM, authHeader, { step: 'PolChange Additional Info to Purchase Screen' });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        const isEmailAddressVerificationRequired = () => {
            const hasEmailAddress = !_.isNil(primaryDriverEmail.value) &&  primaryDriverEmail.value !== '';
            return hasEmailAddress || isElectronicChosen;
        };
        
        driversVM.forEach((driver) => {
            driver.requiredOnAddtlInfo_Ext = true;
            driver.value.storedEmailAddress_ML = driver.value.person.emailAddress_ML
            if (driver.value.spouseID_ML) {
                const spouseDriverVM = driversVM.find((spouse) => spouse.person.publicID.value === driver.value.spouseID_ML);
                driver.spouse = spouseDriverVM ? spouseDriverVM.person : undefined;
                if (driver.value.spouse) {
                    driver.value.spouse.storedEmailAddress_ML = driver.value.spouse.emailAddress_ML;
                }
            }
            if (isRatedDriver(driver) || isExcludedDriver(driver)) {
                driver.value.isLicenseNumberRequired_ML = true;
            }
            if (isExcludedDriver(driver) && (isInGA(addressVM) || isInUT(addressVM) || isInOR(addressVM))) {
                driver.value.excludedDriverEmailRequired_ML = true;
            }
            if (isExcludedDriver(driver)) {
                driver.value.isAgeFirstLicensedRequired_ML = true;
            }
            driver.value.editMode_ML = true;

            if (isPrimaryDriver(driver)) {
                driver.value.reEnterEmailRequired_ML = isEmailAddressVerificationRequired();
            }

            if (driver.requiresProof_ML.value === true) {
                driver.value.isCFRInfoRequired_ML = true;
                driver.editMode_ML = true;
                if ((driver.requestingState_ML.value === null || typeof driver.requestingState_ML === 'undefined') && addressVM !== null) {
                    const stateExistsInTypeList = driver.requestingState_ML.aspects.availableValues.some((state) => {
                        return state.code === endorsementVM.lobData.personalAuto.policyAddress_ML.state.value.code;
                    });
                    driver.requestingState_ML = stateExistsInTypeList ? addressVM.state.value : null;
                }
            }
            driver.showSr22 = driver.requiresProof_ML.value;
        });

        initPrimaryVehicleQuestion();

        MLPublicContactsService.getMortgageeContacts(authHeader).then((data) => {
            setPotentialContacts(data);
        });

        EndorsementService.getAdditionalInterestCandidatesML(endorsementVM.jobID.value, authHeader).then((candidates) => {
            setAdditionalInterestAvailableCandidatesML(candidates);
        });

        verifyAndSkipPage();

    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);    

    const checkForMissingAnnualMileage = () => {
        clearBlockAlerts('annualMileage');

        const hasMissingAnnualMileage = vehiclesVM.some(vehicle => {
            const annualMileage = vehicle.annualMileage?.value;
            const vendorAnnualMileage = vehicle.vendorAnnualMileage_ML?.value;
            const annualMileageMissing = annualMileage === undefined || annualMileage === null || annualMileage === '';
            const vendorAnnualMileageMissing = vendorAnnualMileage === undefined || vendorAnnualMileage === null || vendorAnnualMileage === '';
            const isPrivatePassengerNP = vehicle.category_ML === 'private_passenger_np'
            
            return annualMileageMissing && vendorAnnualMileageMissing && isPrivatePassengerNP;
        });
        if (!hasMissingAnnualMileage) {
            return false;
        }
        addErrorAlert(messages.missingAnnualMileage, 'annualMileage');
        return true;
    };
 

    const onNext = () => {
        clearAlerts();
        
        if (checkForMissingAnnualMileage()) { return false }

        if (isPageValid() && (hasNamedNonOwner || checkIfVinsVerified() || vehiclesExistWithYearPre1981_ML()) && isComponentValid) {
            saveAndQuote();

            if (isGPA) {
                PolChangeTrackEvent('Endorsement Step Submitted', false, endorsementVM, authHeader, {
                    step: 'PolChange Additional Info to Purchase Screen',
                    action: 'Additional Info to Purchase Submitted'
                });
            }

        } else {
            setShowErrors(true);
            if (!validateVehiclesSerialNumber(vehiclesVM)) {
                window.scrollTo(0, 0);
            }else{
                setErrorTimestamp(errorTimestamp+1);
            }
        }
    };

    const getAvailableValuesFinanceCategory = (availableValues) => {
        return _.orderBy(availableValues.map((type) => ({
            code: type.code,
            name: translator({id: type.name, defaultMessage: type.name})
        })), ['code'], ['asc']);
    }

    const getVehiclesOverrides = () => {
        const overrides = vehiclesVM.map((vehicleVM, index) => {
            return {
                [`vehicleName${index}`]: {
                    content: `${getVehicleName(vehicleVM)} ${translator(messages.vehicleX, { vehicleNumber: vehicleVM.vehicleNumber.value })}`
                },
                [`vinContainer${index}`]: {
                    visible: !isCategoryTrailerGolfCartOrLowSpeed(vehicleVM)
                },
                [`vinLookupComponent${index}`]: {
                    vehicleVM,
                    quoteID: endorsementVM.jobID,
                    path: `lobData.personalAuto.coverables.vehicles.children[${index}]`,
                    isVinDuplicated,
                    onValueChange: (value, path) => {
                        _.set(endorsementVM, path, value);
                        refreshData();
                    },
                    updateIsValidVin: (vinValidFlag) => {
                        vehicleVM.isValidVin = vinValidFlag;
                    },
                    index,
                    onValidate,
                    showValidationErrors: showErrors,
                    updateVehicleDetails: refreshData,
                    isGpa: true,
                    isModernProductPersonalAuto
                },
                [`serialNumberContainer${index}`]: {
                    visible: isCategoryTrailerGolfCartOrLowSpeed(vehicleVM)
                },
                [`financeCategory${index}`]: {
                    value: _.get(vehicleVM, 'financeCategory_ML.value.code'),
                    availableValues: getAvailableValuesFinanceCategory(_.get(vehicleVM, 'financeCategory_ML.aspects.availableValues', []))
                },
                [`additionalInterestListContainer${index}`]: {
                    visible: isFinancedOrLeased(vehicleVM)
                },
                [`additionalInterestListComponent${index}`]: {
                    model: vehicleVM.additionalInterests,
                    updateViewModel: refreshData,
                    onValueChange: (value, path) => {
                        _.set(endorsementVM, path, value);
                        refreshData();
                    },
                    initialPotentialContact: potentialContacts,
                    path: `lobData.personalAuto.coverables.vehicles.children[${index}]`,
                    showValidationErrors: showErrors,
                    onValidate,
                    additionalInterestAvailableCandidatesML
                },
            }          
        });

        return Object.assign({}, ...overrides);
    };

    const resetFinancialCertificate = (driverVM) => {
        if (!driverVM.requiresProof_ML.value) {
            driverVM.caseNumber_ML.value = '';
            driverVM.cfreffDate_ML.value = MLLocalDateUtil.date2LocalDate(new Date());
            driverVM.requestingState_ML.value = null;
        } else if (_.isNil(driverVM.requestingState_ML.value) && !_.isNil(addressVM.value)) {
            driverVM.value.requestingState_ML = addressVM.value.state;
        }
    };

    const validateEmailAddress = (driverVM) => {
        const account = driverVM;
        let emailAddress1 =  _.get(account, 'emailAddress_ML.value');
        if(emailAddress1){
            emailAddress1 = emailAddress1.trim();
        }
        let isValidEmailEntred =  /^[-!#$%&'*+/0-9=?A-Z^_a-z`{|}~](\.?[-!#$%&'*+/0-9=?A-Z^_a-z`{|}~])*@[a-zA-Z0-9](-*\.?[a-zA-Z0-9])*\.[a-zA-Z](-?[a-zA-Z0-9])+$/.test(emailAddress1)
        if(isValidEmailEntred){
            const checkEmailVerified_Ext = AccountService.checkEmailVerified_Ext(
                emailAddress1,
                authHeader
            );
            showProgressDialog(platformMessages.loading, checkEmailVerified_Ext, true);
            checkEmailVerified_Ext.then((res) => {
                hideProgressDialog(platformMessages.loading, checkEmailVerified_Ext, true);
                console.log("Email verification Api Called");
                if(res === true){
                    _.set(account, 'emailVerified_Ext.value', true);
                    _.set(account, 'emailAddress_ML.value', emailAddress1);
                }
                else{
                    _.set(account, 'emailVerified_Ext.value', false);
                    _.set(account, 'emailAddress_ML.value', emailAddress1 + ' ');
                }
            }).catch((e) => {
                clearBlockAlerts('invalidEmail');
                addWarningAlert(translator(messages.invalidEmail), 'invalidEmail');
                _.set(account, 'emailAddress_ML.value', emailAddress1);
                _.set(account, 'emailVerified_Ext.value', false);
                hideProgressDialog(platformMessages.loading, checkEmailVerified_Ext, true);
                console.log(e);
                window.scrollTo(0, 0);
            });
        }
        
    };

    const emailTrim = (driverVM, event) => {
        let currEmail = _.get(driverVM, 'emailAddress_ML.value');
        if(currEmail !== "" && currEmail !== undefined){
            currEmail = currEmail.trim();
            _.set(driverVM, 'emailAddress_ML.value', currEmail);
        }
        let email = event.target.value;
        if(email !== "" && email !== undefined){
            email = email.trim();
            event.target.value = email;
        }
    }

    const getDriversOverrides = () => {
        const overrides = driversVM.map((driverVM, index) => {
            const realIdx = endorsementVM.lobData.personalAuto.coverables.drivers.value.indexOf(driverVM.value);
            const basePath = `lobData.personalAuto.coverables.drivers.children[${realIdx}]`
            return {
                [`driverName${index}`]: {
                    content: `${getDriverFullName(driverVM)} ${isRatedDriver(driverVM) ? translator(messages.driverX, { driverNumber: index+1}) : ''} ${isExcludedDriver(driverVM) ? translator(messages.excludedDriverX, { driverNumber: index+1}) : ''}`
                },
                [`licenseNumberContainer${index}`]: {
                    visible: !isHouseholdDriver(driverVM)
                },
                [`licenseNumber${index}`]: {
                    path: `${basePath}.licenseNumber`
                },
                [`licenseStateContainer${index}`]: {
                    visible: isExcludedDriver(driverVM)
                },
                [`licenseState${index}`]: {
                    path: `${basePath}.licenseState`
                },
                [`ageFirstLicensedContainer${index}`]: {
                    visible: isExcludedDriver(driverVM) && driverVM.ageFirstLicensed_ML.aspects.required
                },
                [`ageFirstLicensed${index}`]: {
                    path: `${basePath}.ageFirstLicensed_ML`
                },
                [`requiresProofContainer${index}`]: {
                    visible: isRatedDriver(driverVM) && driverVM.showSr22
                },
                [`requiresProof${index}`]: {
                    onValueChange: (value, path) => {
                        _.set(endorsementVM, path, value);
                        resetFinancialCertificate(driverVM)
                        refreshData();
                    },
                    path: `${basePath}.requiresProof_ML`
                },      
                [`requestingStateContainer${index}`]: {
                    visible: driverVM.requiresProof_ML.value
                },
                [`requestingState${index}`]: {
                    path: `${basePath}.requestingState_ML`
                },
                [`reasonRequiredContainer${index}`]: {
                    visible: driverVM.reasonRequired_Ext.aspects.required
                },
                [`reasonRequired${index}`]: {
                    path: `${basePath}.reasonRequired_Ext`
                },
                [`certEffDateContainer${index}`]: {
                    visible: driverVM.requiresProof_ML.value
                },
                [`certEffDate${index}`]: {
                    path: `${basePath}.cfreffDate_ML`
                },
                [`caseNumberContainer${index}`]: {
                    visible: driverVM.requiresProof_ML.value && !_.isNil(driverVM.requestingState_ML.value)
                },
                [`caseNumber${index}`]: {
                    path: `${basePath}.caseNumber_ML`
                },
                [`emailContainer${index}`]: {
                    visible: showDriverEmailAddress(driverVM),
                    onBlur: (value, path) => {
                        validateEmailAddress(driverVM.person)
                    },
                    onFocus: (event) => emailTrim(driverVM.person, event)
                },
                [`email${index}`]: {
                    path: `${basePath}.person.emailAddress_ML`,
                    disabled: disableDriverEmailField(driverVM)
                },
                [`spouseEmailContainer${index}`]: {
                    visible: showSpouseEmailAddress()
                },
                [`spouseEmail${index}`]: {
                    path: showSpouseEmailAddress() ? `lobData.personalAuto.coverables.drivers.children[${primaryDriverSpouseIndex}].person.emailAddress_ML` : undefined,
                    disabled: disableSpouseEmailAddress()
                }
            }          
        });

        return Object.assign({}, ...overrides);
    }

    const overrideProps = {
        pageContainer: {
            className: hidePage ? styles.displayNone : ''
        },
        vehiclesIterable: {
            data: vehiclesVM,
            visible: !hasNamedNonOwner
        },
        driversIterable: {
            data: driversVM
        },
        nextDescription: {
            content: (
                <FormattedMessage 
                    {...messages.nextDesc}
                    values={{
                        reviewAdditionalActivity: <strong>{ translator(messages.reviewAdditionalActivity) }</strong>,
                        mvrsMayBeRun: <strong>{ translator(messages.mvrsMayBeRun) }</strong>
                    }}
                />
            )
        },
        stickyContinueSection: {
            defaultContinueSectionId: 'footer',
            nextDescription:(
                <FormattedMessage 
                    {...messages.nextDesc}
                    values={{
                        reviewAdditionalActivity: <strong>{ translator(messages.reviewAdditionalActivity) }</strong>,
                        mvrsMayBeRun: <strong>{ translator(messages.mvrsMayBeRun) }</strong>
                    }}
                />
            ),
            onNext
        },
        ...getVehiclesOverrides(),
        ...getDriversOverrides()
    };

    const resolvers = {
        resolveCallbackMap: {
            onNext
        },
        resolveClassNameMap: styles
    }

    return (
        <WizardPage
            policyChangePageHeaderML={messages.additionalDetailsHeader}
            showCancel={false}
            showPrevious={false}
            showNext={false}
        >
            <ViewModelForm
                uiProps={metadata.pageContent}
                overrideProps={overrideProps}
                callbackMap={resolvers.resolveCallbackMap}
                classNameMap={resolvers.resolveClassNameMap}
                model={endorsementVM}
                onModelChange={refreshData}
                onValidationChange={onValidate}
                showErrors={showErrors}
            />
            <ScrollToError counter={errorTimestamp} />
        </WizardPage>
    );
}

AdditionalInfoToPurchasePagePolChange.propTypes = wizardProps;
export default AdditionalInfoToPurchasePagePolChange;